import { APIReferenceType, ArrowType, LineType } from '@ardoq/api-types';
import { GraphItem, GraphItemMetadata } from './GraphItem';
import SparkMD5 from 'spark-md5';
import { DiffType } from '@ardoq/data-model';
import { DIFF_TYPE, IS_PARENT_OF } from '@ardoq/global-consts';
import { PARENT_CHILD_LINE_TYPE } from './consts';
import { ItemLabels } from '../types';
import { GraphEdgeBase } from './GraphEdgeBase';
import { getGraphNodes, getParentChildEdgeDiffType } from './utils';
import { ComponentGraphParentChildEdgeModel } from './types';
import { GraphInterface } from '../graphInterface/graphInterface';
import { createReferenceIdFromParentAndChildId } from '../utils/grouping/parentChildUtils';
import { type I18nLocale } from '@ardoq/i18n';
import { isParentOfLabel } from '../labels/parentChildLabels';

export type ParentChildEdgeMetadata = {
  sourceVisualDiffType: DiffType;
  targetVisualDiffType: DiffType;
  hasScenarioParentChanged: boolean;
  itemLabels: ItemLabels;
};

type ParentChildEdgeConstructorArgs = {
  modelId: string;
  sourceGraphNodeId: string;
  targetGraphNodeId: string;
  sourceModelId: string;
  targetModelId: string;
  id?: string;
  metadata: ParentChildEdgeMetadata;
};

const lineColor = '#000';
const lineBeginning = ArrowType.NONE;
const lineEnding = ArrowType.DIAMOND;

type GetParentChildEdgeMetadataArgs = {
  viewInterface: GraphInterface;
  sourceId: string;
  targetId: string;
  edgeLabelsData: Map<string, ItemLabels>;
  graphNodeMap: Map<string, GraphItem>;
  edgeId: string;
  i18nLocale: I18nLocale;
  hasScenarioParentChanged?: boolean;
};

export const getParentChildEdgeMetadata = ({
  viewInterface,
  sourceId,
  targetId,
  edgeLabelsData,
  graphNodeMap,
  edgeId,
  i18nLocale,
  hasScenarioParentChanged = false,
}: GetParentChildEdgeMetadataArgs): ParentChildEdgeMetadata => {
  const { sourceGraphNode, targetGraphNode } = getGraphNodes(
    sourceId,
    targetId,
    graphNodeMap
  );

  const sourceVisualDiffType =
    viewInterface.getComponentAttribute(sourceGraphNode.modelId, DIFF_TYPE) ??
    DiffType.NONE;

  const targetVisualDiffType =
    viewInterface.getComponentAttribute(targetGraphNode.modelId, DIFF_TYPE) ??
    DiffType.NONE;

  const itemLabels = edgeLabelsData.get(edgeId) ?? {
    mainLabel: isParentOfLabel(i18nLocale),
  };

  return {
    sourceVisualDiffType,
    targetVisualDiffType,
    hasScenarioParentChanged,
    itemLabels,
  };
};

/**
 * Arguments for creating a synthetic parent-child edge to represent component hierarchies.
 */
type CreateParentChildEdgeArgs = {
  /**
   * Synthetic identifier in the format parentComponentId-childComponentId, typically generated in raw graph items.
   * If not provided, will be automatically generated from the source and target nodes' modelIds.
   */
  modelId?: string;
  /** The graph item ID for this edge, typically modelId + suffix from the raw graph item */
  id: string;
  /** The graph item ID of the parent node (source of the edge) */
  sourceId: string;
  /** The graph item ID of the child node (target of the edge) */
  targetId: string;
  /** Metadata containing visual diff types and labels for the edge */
  metadata: ParentChildEdgeMetadata;
  /** Map of all graph nodes (both components and groups) by their graph item IDs */
  graphNodeMap: Map<string, GraphItem>;
};

/*
 * Creates a synthetic parent-child edge to represent component hierarchies in the graph.
 * These edges are not actual references in the data model but are generated on-the-fly
 * to enable consistent graph traversal of component parent-child relationships.
 */
export class ComponentGraphParentChildEdge
  extends GraphEdgeBase
  implements ComponentGraphParentChildEdgeModel
{
  static create({
    id,
    modelId: maybeModelId,
    sourceId,
    targetId,
    graphNodeMap,
    metadata,
  }: CreateParentChildEdgeArgs) {
    const sourceGraphNode = graphNodeMap.get(sourceId);
    const targetGraphNode = graphNodeMap.get(targetId);

    if (!sourceGraphNode || !targetGraphNode) {
      throw new Error(
        'Trying to create an edge without according source and/or target nodes'
      );
    }

    const modelId =
      maybeModelId ||
      createReferenceIdFromParentAndChildId(
        sourceGraphNode.modelId,
        targetGraphNode.modelId
      );

    return new ComponentGraphParentChildEdge({
      modelId,
      sourceGraphNodeId: sourceGraphNode.id,
      targetGraphNodeId: targetGraphNode.id,
      sourceModelId: sourceGraphNode.modelId,
      targetModelId: targetGraphNode.modelId,
      id,
      metadata,
    });
  }

  static getType(i18nLocale?: I18nLocale): APIReferenceType {
    return {
      name: i18nLocale ? isParentOfLabel(i18nLocale) : IS_PARENT_OF,
      lineBeginning,
      lineEnding,
      color: lineColor,
      line: PARENT_CHILD_LINE_TYPE,
      returnsValue: false,
      id: -1,
      svgStyle: null,
    };
  }

  readonly source: string;
  readonly target: string;
  readonly sourceModelId: string;
  readonly targetModelId: string;
  protected metadata: ParentChildEdgeMetadata;

  private constructor({
    modelId,
    sourceGraphNodeId,
    targetGraphNodeId,
    sourceModelId,
    targetModelId,
    id,
    metadata,
  }: ParentChildEdgeConstructorArgs) {
    const graphItemMetadata: GraphItemMetadata = {
      isComponent: false,
      isReference: false,
      isGroup: false,
    };
    super(modelId, graphItemMetadata, id);
    this.source = sourceGraphNodeId;
    this.target = targetGraphNodeId;
    this.sourceModelId = sourceModelId;
    this.targetModelId = targetModelId;
    this.metadata = metadata;
    this.hash = SparkMD5.hash(this.getRawHash());
  }

  override getLabel(): string {
    return this.metadata.itemLabels.mainLabel || this.label || '';
  }

  override hasURLFields() {
    return false;
  }

  override getCSS() {
    return 'parent-child-reference';
  }
  override getLineBeginning() {
    return lineBeginning;
  }
  override getLineEnding() {
    return lineEnding;
  }
  override getLine(): LineType {
    return PARENT_CHILD_LINE_TYPE;
  }
  override getItemLabels(): ItemLabels | undefined {
    return this.metadata.itemLabels;
  }
  override setItemLabels(itemLabels: ItemLabels): void {
    this.metadata.itemLabels = itemLabels;
  }
  override getVisualDiffType() {
    const { sourceVisualDiffType, targetVisualDiffType } = this.metadata;
    return getParentChildEdgeDiffType(
      sourceVisualDiffType,
      targetVisualDiffType,
      this.hasScenarioParentChanged()
    );
  }

  hasScenarioParentChanged(): boolean {
    return this.metadata.hasScenarioParentChanged;
  }

  setHasScnarioParentChanged(hasChanged: boolean) {
    this.metadata.hasScenarioParentChanged = hasChanged;
  }

  protected getRawHash() {
    return `${super.getRawHash()}${this.sourceModelId}${this.targetModelId}`;
  }
}
